import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { SessionUser, UserRole } from '@sync-tracker/shared';
import { api } from '@/api';
import { realtime } from '@/api/realtime';

const RANK: Record<UserRole, number> = { viewer: 0, operator: 1, admin: 2 };

export const useAuth = defineStore('auth', () => {
  const user = ref<SessionUser | null>(null);
  const checked = ref(false);

  async function check() {
    try {
      user.value = (await api.auth.me()).user;
    } catch {
      user.value = null;
    } finally {
      checked.value = true;
    }
    return user.value;
  }

  async function login(login: string, password: string) {
    user.value = (await api.auth.login(login, password)).user;
    return user.value;
  }

  async function logout() {
    try {
      await api.auth.logout();
    } finally {
      user.value = null;
      realtime.disconnect();
    }
  }

  const can = (role: UserRole) => !!user.value && RANK[user.value.role] >= RANK[role];
  const isAdmin = computed(() => can('admin'));
  const isOperator = computed(() => can('operator'));

  return { user, checked, check, login, logout, can, isAdmin, isOperator };
});
